import './style.scss';

import { type FC, useEffect, useRef, useState } from 'react';

import type { ICancellationStatus } from '../../../hooks/useCancellationStatus';
import type { ICallSection } from '../../utils/right-section';
import { CameraGlyph, evidenceAllowed, type IPuCancellationData } from './shared';

export const PANEL_TITLE = 'Polling unit cancelled';

/** Opens the panel from any entry point (home row, stamped card, reports hub tile). */
export const openCancellationPanel = (
	rsProps: { callSection: (args: ICallSection<any>) => void } | undefined,
	data: IPuCancellationData
) => rsProps?.callSection({ action: 'create', component: 'pu-cancellation', title: PANEL_TITLE, max: true, data });

const Chevron = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 16 16'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M6 3l5 5-5 5'
			stroke='currentColor'
			strokeWidth='1.5'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

/** True while the agent may still withdraw — drives whether the home card offers it. */
export const useWithdrawOpen = (status: ICancellationStatus): boolean => {
	const row = status.snapshot.row;
	const record = status.record;
	const localOnly = !!record && record.report.state !== 'sent' && record.report.state !== 'refused' && !record.withdrawnAt;
	const windowMin = status.caps?.mode === 'v2' ? status.caps.caps.withdrawWindowMinutes : 10;
	const deadline =
		row?.source === 'Agent' && row.reviewStatus !== 'Confirmed' && !record?.alreadyCancelled
			? row.withdrawableUntil
				? Date.parse(row.withdrawableUntil)
				: Date.parse(row.recordedAt) + windowMin * 60_000
			: NaN;
	const [now, setNow] = useState(Date.now);
	const open = Number.isFinite(deadline) && deadline > now;
	useEffect(() => {
		if (!open) return;
		const id = window.setTimeout(() => setNow(Date.now()), Math.min(deadline - now + 50, 60_000));
		return () => window.clearTimeout(id);
	}, [open, deadline, now]);
	return status.snapshot.active && (localOnly || open);
};

interface IHomeProps {
	status: ICancellationStatus;
	/** `photos` = captured before the panel opened (camera-first), attached to the new report. */
	open: (mode: IPuCancellationData['mode'], photos?: File[]) => void;
}

/**
 * Under the assignment card. Before filing: one quiet outlined row. After: the card itself
 * carries the stamp, and this row becomes the report's actions.
 */
export const CancellationHomeRow: FC<IHomeProps> = ({ status, open }) => {
	const withdrawOpen = useWithdrawOpen(status);
	const cameraRef = useRef<HTMLInputElement>(null);
	const { snapshot, record, caps } = status;

	if (!snapshot.active) {
		// Two siblings, never nested: the row opens the reason step; the camera captures first
		// (the moment may not last) and then opens the reason step with the photo attached.
		return (
			<div className='pc-entry-wrap'>
				<button
					type='button'
					className='pc-entry'
					onClick={() => open('report')}
				>
					<span className='pc-entry__text'>Polling unit cancelled by INEC?</span>
					<span className='pc-entry__hint'>Report it</span>
					<Chevron />
				</button>
				<button
					type='button'
					className='pc-entry-camera'
					onClick={() => cameraRef.current?.click()}
					aria-label='Take a photo first, then report the cancellation'
				>
					<CameraGlyph />
				</button>
				<input
					ref={cameraRef}
					type='file'
					accept='image/*'
					capture='environment'
					className='d-none'
					data-testid='pc-entry-camera-input'
					onChange={({ target }) => {
						const photos = Array.from(target.files ?? []).filter((f) => f.type.startsWith('image/'));
						target.value = '';
						if (photos.length) open('report', photos);
					}}
				/>
			</div>
		);
	}

	const canAddEvidence = !snapshot.localOnly && !record?.baseline && evidenceAllowed(caps) && !!snapshot.row;

	return (
		<div
			className='pc-home-actions'
			role='group'
			aria-label='Cancellation report'
		>
			{/* Review state reads on the card's Cancelled badge, beside the status it qualifies. */}
			{canAddEvidence ? (
				<button
					type='button'
					className='pc-btn pc-btn--ghost'
					onClick={() => open('add-evidence')}
				>
					Add evidence
				</button>
			) : null}
			{withdrawOpen ? (
				<button
					type='button'
					className='pc-btn pc-btn--ghost'
					onClick={() => open('report')}
				>
					Withdraw
				</button>
			) : null}
			<button
				type='button'
				className='pc-btn pc-btn--ghost'
				onClick={() => open('report')}
			>
				View report
			</button>
		</div>
	);
};
